import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { GemsBalance } from '@sorare/core/src/components/commonGame/GemsBalance';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { getCommonCardShardsCount } from '@sorare/core/src/lib/currentUser';
import { useIsCurrentCardSet } from '@sorare/core/src/set';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardPackGroup } from '#src/components/commonCard/BuyPacks/CardPackGroup/index.tsx';

import type { LegacyPacksPage_marketRoot } from './__generated__/index.graphql.ts';

const Root = styled.div`
  position: relative;
  isolation: isolate;
  container-type: inline-size;
  padding: 0 0 var(--quadruple-unit);
`;

const Header = styled(Horizontal)`
  padding: var(--double-unit);
  justify-content: flex-end;
  position: sticky;
  top: var(--current-stack-height);
  z-index: 1;
  @media ${laptopAndAbove} {
    padding: var(--triple-unit) var(--double-unit);
  }
`;

type Props = {
  refetch: () => Promise<unknown>;
  marketRoot: LegacyPacksPage_marketRoot;
};

export const LegacyPacksPage = ({ refetch, marketRoot }: Props) => {
  const isCurrentCardSet = useIsCurrentCardSet();
  const { currentUser } = useCurrentUserContext();

  const commonSetCardPackGroups = marketRoot.cardPackGroups?.filter(
    group => !isCurrentCardSet(group.cardSet?.slug)
  );

  return (
    <Root>
      <PullToRefetch refetch={refetch}>
        <Vertical gap={3}>
          <Header>
            <EssenceBalance
              value={
                getCommonCardShardsCount(currentUser, 'common-base-set') || 0
              }
              rarity={Rarity.common}
              cardSetSlug="common-base-set"
            />
            <GemsBalance balance={currentUser?.gemsBalances[0]?.amount || 0} />
          </Header>

          <Container maxWidth={1320}>
            <Vertical gap={3}>
              {commonSetCardPackGroups?.map(group => (
                <CardPackGroup key={group.id} cardPackGroup={group} />
              ))}
            </Vertical>
          </Container>
        </Vertical>
      </PullToRefetch>
    </Root>
  );
};

LegacyPacksPage.fragments = {
  marketRoot: gql`
    fragment LegacyPacksPage_marketRoot on MarketRoot {
      id
      cardPackGroups(sport: $sport) {
        id
        cardSet {
          slug
        }
        cardPacks {
          slug
        }
        ...CardPackGroup_cardPackGroup
      }
    }
    ${CardPackGroup.fragments.cardPackGroup}
  ` as TypedDocumentNode<LegacyPacksPage_marketRoot>,
};
